import camelcaseKeys from "camelcase-keys";
import Decimal from "decimal.js-light";
import { fromJS } from "immutable";
import { round } from "lodash";

import * as priceApi from "../../api/prices";

import ReduxModule from "./abstract/ReduxModule";
import alerts from "./alerts";
import * as helpers from "./helpers/price";
import { withMutations } from "./helpers/reducerWrappers";

Decimal.config({ rounding: Decimal.ROUND_HALF_UP });

const ACTIONS = {
  CLEAR_ACTIVE_ORDER: "Clear active order",
  RESET_DRAWINGS: "Reset drawings",
  SET_DATA_AFTER_SAVING_DRAWINGS: "Set data after saving drawings",
  CREATE_NEW_ORDER: "Create new order",
  SET_UIDS: "Set uids",
  OPEN_ORDER: "Load data from order",

  ADD_NEW_DRAWING: "Add new drawing",
  DUPLICATE_DRAWING: "Duplicate drawing",
  SET_ACTIVE_DRAWING_TEMP_UID: "Set active drawing temp uid",
  DELETE_DRAWING: "Delete drawing",
  UNDO_DELETE_DRAWING: "Undo delete drawing",

  GET_PRICES_BY_PRODUCT: "Get prices by product",
  SET_PRICE_PER_METER_DRAWING: "Set price per meter drawing",
  SET_TOTAL_PRICE_DRAWING: "Set total price drawing",
  SET_ADDITIONAL_PRICES: "Set additional prices",
  SET_DISCOUNT_PRICES: "Set discount prices",
  SET_MARKUP_PRICES: "Set markup prices",
  SET_ORDER_PRICES_FROM_SERVER: "Set order prices from server",
  CALC_PRICE_DRAWINGS: "Calc price drawings",

  SWITCH_CUSTOM_PRICE: "Switch custom price",
  SWTICH_CUSTOM_PRICE_PER_METER: "Switch custom price per meter",
  CHANGE_DRAWING_PRICE: "Change drawing price",
};

class AuthModule extends ReduxModule {
  getNamespace() {
    return "[Prices]";
  }

  getInitialState() {
    return {
      activeDrawingPrices: null,
      startStateDrawings: null, // items: {}
      startStateOrder: null, // orderPrices: {}
      items: {}, // [drawingTempUid]: this.initialDrawingPricesState()
      orderPrices: fromJS(this.initialOrderPricesState()),
      activeDrawingUid: null,
      isChangedPrice: false,
      dataServer: {
        isPending: false,
        items: [],
      },
    };
  }

  initialDrawingPricesState() {
    return {
      isDeleted: false,
      pricePerMeter: "0.00",
      totalPrice: "0.00",
      customPrice: null,
      customPricePerMeter: null,
      data: [], // dataServer.items
    };
  }

  initialOrderPricesState() {
    return {
      additionalPrices: [],
      discountPrices: [],
      markupPrices: [],

      sumAdditionalPrices: "0.00",
      sumDrawingsPrices: "0.00",
      sumDrawingsCustomPrices: "0.00",
      gstDrawingsPrices: "0.00",

      labourTotalPrice: "0.00",
      subTotal: "0.00",
      margin: "0.00",
      marginPercentage: "0",
      estimatedMargin: "0.00",
      estimatedMarginPercentage: "0.00",
      GST: "0.00",
      totalPrice: "0.00",
    };
  }

  generateOrderDataFromServer({ drawingsData = [], drawingsItems = [], orderPrices, taxRate = 0 }) {
    const items = {};
    const prices = {};

    drawingsData.forEach((drawing) => {
      const drawingUid = drawing.uid;

      drawingsItems.forEach((item) => {
        if (item.drawing === drawingUid) {
          if (prices[drawingUid]) {
            prices[drawingUid].totalPrice =
              parseFloat(item.total_value) + prices[drawingUid].totalPrice;

            prices[drawingUid].customPrice =
              item.custom_value !== null
                ? parseFloat(item.custom_value) + prices[drawingUid].customPrice
                : item.custom_value;

            prices[drawingUid].customPricePerMeter = item.custom_value_lm
              ? parseFloat(item.custom_value_lm)
              : null;
          } else {
            prices[drawingUid] = {};

            prices[drawingUid].totalPrice = item.total_value
              ? parseFloat(item.total_value)
              : "0.00";

            prices[drawingUid].customPrice = item.custom_value
              ? parseFloat(item.custom_value)
              : null;

            prices[drawingUid].customPricePerMeter = item.custom_value_lm
              ? parseFloat(item.custom_value_lm)
              : null;
          }

          const { totalPrice, customPrice, customPricePerMeter } = prices[drawingUid];

          items[drawingUid] = {
            pricePerMeter: item.value,
            totalPrice: round(totalPrice, 2).toFixed(2),
            customPrice: customPrice !== null ? round(customPrice, 2).toFixed(2) : null,
            customPricePerMeter:
              customPricePerMeter !== null ? round(customPricePerMeter, 2).toFixed(2) : null,
            data: [],
          };
        }
      });

      if (!items[drawingUid]) {
        items[drawingUid] = this.initialDrawingPricesState();
      }
    });

    const updatedOrderPrices = { ...orderPrices };

    // console.log("orderPrices", orderPrices);
    // orderPrices.additionalPrices is for the delivery fee
    if (orderPrices.additionalPrices) {
      updatedOrderPrices.sumAdditionalPrices = orderPrices.additionalPrices
        .reduce((sum, item) => Decimal(sum).plus(item.value), Decimal(0))
        .toDecimalPlaces(2)
        .toFixed(2);
    }

    // Commented this since discount now a running stackable discount.
    // due to the combination of currency fixed based and percentage based discount.
    // if (orderPrices.discountPrices) {
    //   updatedOrderPrices.sumDiscountPercent = orderPrices.discountPrices
    //     .filter((item) => item.percent)
    //     .reduce((sum, item) => Decimal(sum).plus(item.percent), Decimal(0))
    //     .toDecimalPlaces(2)
    //     .toFixed(2);
    // }

    const { sumDrawingsPrices, sumDrawingsCustomPrices, gstDrawingsPrices } =
      helpers.calcPricesDrawings(items, taxRate);
    updatedOrderPrices.sumDrawingsPrices = sumDrawingsPrices;
    updatedOrderPrices.sumDrawingsCustomPrices = sumDrawingsCustomPrices;
    updatedOrderPrices.gstDrawingsPrices = gstDrawingsPrices;

    return {
      items,
      orderPrices: updatedOrderPrices,
    };
  }

  createNewOrder = (_state) => {
    _state.merge(fromJS(this.getInitialState()));
  };

  setUids = (_state, { additionalPrices, discountPrices, markupPrices }) => {
    _state.getIn(["orderPrices", "additionalPrices"]).forEach((_, i) => {
      _state.setIn(["orderPrices", "additionalPrices", i, "uid"], additionalPrices[i].uid);
    });

    _state.getIn(["orderPrices", "discountPrices"]).forEach((_, i) => {
      _state.setIn(["orderPrices", "discountPrices", i, "uid"], discountPrices[i].uid);
    });

    _state.getIn(["orderPrices", "markupPrices"]).forEach((_, i) => {
      _state.setIn(["orderPrices", "markupPrices", i, "uid"], markupPrices[i].uid);
    });
  };

  openOrder = (_state, data) => {
    _state.merge(fromJS(this.getInitialState()));

    let { items, orderPrices } = data;
    items = fromJS(items);
    orderPrices = fromJS(orderPrices);

    _state.set("items", items);
    _state.set("startStateDrawings", items);

    const startStateOrder = _state.mergeIn(["orderPrices"], orderPrices);
    _state.set("startStateOrder", startStateOrder.get("orderPrices"));
  };

  // CUS-719: the drawing's product options are committed before this fetch resolves, so until it
  // lands the drawing still holds the PREVIOUS selection's table. Only the newest request per
  // drawing may write that drawing's table; an older (out-of-order) response is dropped.
  latestPriceRequests = {}; // [drawingTempUid]: request id still in flight

  priceRequestId = 0;

  // isReload: the drawing's options did not change (an opened saved drawing, an undone delete),
  // so its current rate is not marked as belonging to old options.
  // drawingUid: the drawing to load for; defaults to the one on screen.
  getPricesProductsThunk = (
    { token, fulfilled, getState, dispatch, pendingAction },
    product,
    { isReload = false, drawingUid = getState().getIn(["prices", "activeDrawingUid"]) } = {}
  ) => {
    const { productType, name, color, thickness, priceLevel } = product || {};

    this.priceRequestId += 1;
    const requestId = this.priceRequestId;
    this.latestPriceRequests[drawingUid] = requestId;
    // Clears the stale table and blocks Done until the matching table is in.
    dispatch(pendingAction({ isPending: true, drawingUid, isOptionChange: !isReload }));

    const settle = (data, failed) => {
      if (this.latestPriceRequests[drawingUid] !== requestId) {
        return; // superseded by a newer request for this drawing
      }
      delete this.latestPriceRequests[drawingUid];

      // The drawing's options may have gone back without a new request (Cancel, Undo, the order
      // reloaded). Then this table belongs to options the drawing no longer has.
      const current = getState().getIn(["drawingsProducts", "items", drawingUid, "product"]);
      const requested = { productType, name, color, thickness, priceLevel };
      const stillWanted =
        current &&
        Object.keys(requested).every(
          (field) => String(current.get(field)) === String(requested[field])
        );

      if (stillWanted) {
        if (data) {
          fulfilled({ data, drawingUid });
        }

        // Re-price the drawing that asked, with its geometry as it is now. A failed request only
        // re-prices if the rate is still the old options' rate: the table is empty, so it drops to
        // 0.00. A saved rate for unchanged options is kept.
        const activeDrawing = getState().getIn(["drawings", "activeDrawing"]);
        const drawing =
          activeDrawing && activeDrawing.get("tempUid") === drawingUid
            ? activeDrawing
            : getState()
                .getIn(["drawings", "items"])
                ?.find((item) => item.get("tempUid") === drawingUid);
        const isRateStale = getState().getIn(["prices", "items", drawingUid, "isRateStale"]);
        if (drawing && (!failed || isRateStale)) {
          dispatch(this.actions.setPricesDrawing(drawing));
        }

        if (failed) {
          dispatch(
            alerts.actions.addAlert({
              type: "danger",
              message: "Couldn't load the flashing price table. Re-select an option to try again.",
            })
          );
        }
      }

      // Still loading if another drawing of this order is waiting for its table.
      const isPending = Object.keys(this.latestPriceRequests).some((uid) =>
        getState().hasIn(["prices", "items", uid])
      );
      dispatch(pendingAction({ isPending }));
    };

    return priceApi
      .getPrice(token, {
        name,
        product_type: productType,
        color,
        thickness,
        price_level: priceLevel,
      })
      .then(
        (response) => {
          settle(response.data, false);
          return response.data;
        },
        () => settle(null, true)
      );
  };

  setPricePerMeterDrawingThunk = ({ dispatch, getState, fulfilled }, newDrawing) => {
    const drawing = newDrawing || getState().getIn(["drawings", "activeDrawing"]);

    let drawingUid;

    if (drawing) {
      const numberOfBends = drawing.get("totalBends");
      const totalGirth = drawing.get("totalGirth");
      const tapers = drawing.get("tapers");

      drawingUid = drawing.get("tempUid");

      fulfilled({
        numberOfBends,
        totalGirth,
        drawingUid,
        tapers,
      });
    }

    dispatch(this.actions.setTotalPriceDrawing(drawingUid));
  };

  switchCustomPriceThunk = ({ dispatch, getState, fulfilled }, customPrice) => {
    fulfilled(customPrice);
    const drawingUid = getState().getIn(["drawings", "activeDrawing", "tempUid"]);
    dispatch(this.actions.changeDrawingPrice(true));
    dispatch(this.actions.setTotalPriceDrawing(drawingUid));
  };

  switchCustomPricePerMeterThunk = ({ dispatch, getState, fulfilled }, customPricePerMeter) => {
    fulfilled(customPricePerMeter);
    const drawingUid = getState().getIn(["drawings", "activeDrawing", "tempUid"]);
    dispatch(this.actions.changeDrawingPrice(true));
    dispatch(this.actions.setTotalPriceDrawing(drawingUid));
  };

  setTotalPriceDrawingThunk = ({ dispatch, getState, fulfilled }, drawingUid) => {
    if (drawingUid) {
      const totalLength = getState().getIn([
        "drawingsItems",
        "itemsLength",
        drawingUid,
        "forPrice",
      ]);
      fulfilled({ totalLength, drawingUid });
    }

    dispatch(this.actions.calcPricesDrawings());
  };

  setAdditionalPricesThunk = ({ fulfilled }, additionalPrices) => {
    fulfilled(additionalPrices);
  };

  setDiscountPricesThunk = ({ dispatch, fulfilled }, discountPrices) => {
    fulfilled(discountPrices);
    dispatch(this.actions.calcPricesDrawings());
  };

  setMarkupPricesThunk = ({ fulfilled }, markupPrices) => {
    fulfilled(markupPrices);
  };

  defineActions() {
    const clearActiveOrder = this.resetToInitialState(ACTIONS.CLEAR_ACTIVE_ORDER);
    const createNewOrder = this.createAction(ACTIONS.CREATE_NEW_ORDER);
    const setUids = this.createAction(ACTIONS.SET_UIDS);
    const openOrder = this.createAction(ACTIONS.OPEN_ORDER);
    const setOrderPricesFromServer = this.createAction(ACTIONS.SET_ORDER_PRICES_FROM_SERVER);

    const resetDrawings = this.createAction(ACTIONS.RESET_DRAWINGS);
    const setDataAfterSavingDrawings = this.createAction(ACTIONS.SET_DATA_AFTER_SAVING_DRAWINGS);
    const addNewDrawing = this.createAction(ACTIONS.ADD_NEW_DRAWING);
    const duplicateDrawing = this.createAction(ACTIONS.DUPLICATE_DRAWING);
    const setDrawingTempUid = this.createAction(ACTIONS.SET_ACTIVE_DRAWING_TEMP_UID);
    const deleteDrawing = this.createAction(ACTIONS.DELETE_DRAWING);
    const undoDeleteDrawing = this.createAction(ACTIONS.UNDO_DELETE_DRAWING);
    const changeDrawingPrice = this.createAction(ACTIONS.CHANGE_DRAWING_PRICE);

    const setPricesDrawing = this.thunkAction(
      ACTIONS.SET_PRICE_PER_METER_DRAWING,
      this.setPricePerMeterDrawingThunk
    );
    const setTotalPriceDrawing = this.thunkAction(
      ACTIONS.SET_TOTAL_PRICE_DRAWING,
      this.setTotalPriceDrawingThunk
    );
    const switchCustomPrice = this.thunkAction(
      ACTIONS.SWITCH_CUSTOM_PRICE,
      this.switchCustomPriceThunk
    );
    const switchCustomPricePerMeter = this.thunkAction(
      ACTIONS.SWTICH_CUSTOM_PRICE_PER_METER,
      this.switchCustomPricePerMeterThunk
    );
    const getPricesProducts = this.thunkAction(
      ACTIONS.GET_PRICES_BY_PRODUCT,
      this.getPricesProductsThunk,
      true,
      false
    );
    const setAdditionalPrices = this.thunkAction(
      ACTIONS.SET_ADDITIONAL_PRICES,
      this.setAdditionalPricesThunk
    );
    const setDiscountPrices = this.thunkAction(
      ACTIONS.SET_DISCOUNT_PRICES,
      this.setDiscountPricesThunk
    );
    const setMarkupPrices = this.thunkAction(ACTIONS.SET_MARKUP_PRICES, this.setMarkupPricesThunk);
    const calcPricesDrawings = this.createAction(ACTIONS.CALC_PRICE_DRAWINGS);

    return {
      clearActiveOrder,
      createNewOrder,
      setUids,
      openOrder,
      setOrderPricesFromServer,

      resetDrawings,
      setDataAfterSavingDrawings,
      addNewDrawing,
      duplicateDrawing,
      setDrawingTempUid,
      deleteDrawing,
      undoDeleteDrawing,

      setPricesDrawing,
      setTotalPriceDrawing,
      switchCustomPrice,
      switchCustomPricePerMeter,
      getPricesProducts,
      setAdditionalPrices,
      setDiscountPrices,
      setMarkupPrices,
      calcPricesDrawings,
      changeDrawingPrice,
    };
  }

  addNewDrawing = (_state, { drawingTempUid, newDrawingTempUid }) => {
    let prices = drawingTempUid ? _state.getIn(["items", drawingTempUid]) : null;

    if (prices && !prices.getIn(["isDeleted"])) {
      prices = prices.set("pricePerMeter", "0.00");
      prices = prices.set("totalPrice", "0.00");
      prices = prices.set("customPrice", null);
      prices = prices.set("customPricePerMeter", null);
    } else {
      prices = fromJS(this.initialDrawingPricesState());
    }

    _state.set("activeDrawingUid", newDrawingTempUid);

    _state.setIn(["items", newDrawingTempUid], prices);
    _state.set("activeDrawingPrices", prices);
  };

  duplicateDrawing = (_state, { drawingTempUid, newDrawingTempUid }) => {
    const items = _state.getIn(["items", drawingTempUid]);
    _state.setIn(["items", newDrawingTempUid], items);
    _state.setIn(["dataServer", "items"], items.get("data"));
  };

  setPricePerMetreDrawing = (_state, drawingOptions) => {
    const { numberOfBends, totalGirth, drawingUid, tapers } = drawingOptions;

    // CUS-719: price a drawing from its own table, never from whichever table loaded last.
    const table = _state.getIn(["items", drawingUid, "data"]) || fromJS([]);
    const value = helpers.findPricePerMetreDrawing({
      numberOfBends,
      totalGirth,
      tapers,
      dataServer: table,
    });
    _state.setIn(["items", drawingUid, "pricePerMeter"], value);
    // Only a loaded table makes the rate current. Priced from an empty one (still loading), the
    // drawing stays marked, so a failed load or an Undo still knows to fix it.
    if (table.size) {
      _state.setIn(["items", drawingUid, "isRateStale"], false);
    }
  };

  setTotalPriceDrawing = (_state, { totalLength, drawingUid }) => {
    const pricePerMeter =
      parseFloat(_state.getIn(["items", drawingUid, "customPricePerMeter"])) >= 0
        ? parseFloat(_state.getIn(["items", drawingUid, "customPricePerMeter"]))
        : _state.getIn(["items", drawingUid, "pricePerMeter"]);
    const totalPrice = helpers.calcTotalPriceDrawing({
      totalLength,
      pricePerMeter,
    });
    _state.setIn(["items", drawingUid, "totalPrice"], totalPrice);

    // A drawing re-priced in the background must not take over the on-screen price block.
    if (drawingUid === _state.get("activeDrawingUid")) {
      _state.set("activeDrawingPrices", _state.getIn(["items", drawingUid]));
    }
  };

  switchCustomPrice = (_state, payload) => {
    const customPrice = payload && parseFloat(payload) >= 0 ? round(payload, 2).toFixed(2) : null;

    const activeDrawingUid = _state.get("activeDrawingUid");
    _state.setIn(["items", activeDrawingUid, "customPrice"], customPrice);
    _state.setIn(["activeDrawingPrices, customPrice"], customPrice);
  };

  switchCustomPricePerMeter = (_state, payload) => {
    const activeDrawingUid = _state.get("activeDrawingUid");

    const customPricePerMeter =
      payload && parseFloat(payload) >= 0 ? round(payload, 2).toFixed(2) : null;

    _state.setIn(["items", activeDrawingUid, "customPricePerMeter"], customPricePerMeter);
    _state.setIn(["activeDrawingPrices, customPricePerMeter"], customPricePerMeter);
  };

  // FAC-6025 BE-as-source-of-truth: map the BE-computed order summary (returned
  // by the save endpoint and re-derived by recalculate_order_total /
  // calculate_margin on every save) onto orderPrices. This is the only writer
  // of the summary keys after an edit — the FE no longer computes them.
  setOrderPricesFromServer = (_state, data) => {
    if (!data) {
      return;
    }

    // The BE guarantees every summary key is a fixed 2dp string ("0.00" when
    // unset), so the values are stored as-is — no null checks or re-formatting.
    _state.mergeIn(
      ["orderPrices"],
      fromJS({
        labourTotal: data.labour_total,
        subTotal: data.sub_total,
        margin: data.margin,
        marginPercentage: data.margin_percentage,
        estimatedMargin: data.estimated_margin || "0.00",
        estimatedMarginPercentage: data.estimated_margin_percentage || "0.00",
        GST: data.gst,
        totalPrice: data.total,
      })
    );
  };

  setDrawingTempUid = (_state, newDrawingTempUid) => {
    _state.set("activeDrawingUid", newDrawingTempUid);

    if (newDrawingTempUid) {
      const activeDrawingPrices = _state.getIn(["items", newDrawingTempUid]);
      _state.setIn(["dataServer", "items"], activeDrawingPrices.get("data"));
      _state.set("activeDrawingPrices", activeDrawingPrices);
    } else {
      _state.set("activeDrawingPrices", null);
    }
  };

  getPricesProducts = (_state, { data, drawingUid }) => {
    this.setDrawingPriceTable(_state, drawingUid, fromJS(camelcaseKeys(data, { deep: true })));
  };

  // Writes the table into the drawing that asked for it (it may no longer be on screen).
  setDrawingPriceTable = (_state, drawingUid, table) => {
    if (_state.hasIn(["items", drawingUid])) {
      _state.setIn(["items", drawingUid, "data"], table);
    }
    if (drawingUid === _state.get("activeDrawingUid")) {
      _state.setIn(["dataServer", "items"], table);
    }
  };

  // CUS-719: a new request means the drawing's current table is for its old options. Empty it
  // so nothing (a point edit, a copy of this drawing) can be priced from it in the meantime.
  // After an option change its rate is the old options' rate until it is re-priced (isRateStale).
  // That flag lives on the drawing so Cancel and Undo put back the right value with it.
  setPricesPending = (_state, { isPending, drawingUid, isOptionChange }) => {
    _state.setIn(["dataServer", "isPending"], isPending);
    if (drawingUid !== undefined) {
      this.setDrawingPriceTable(_state, drawingUid, fromJS([]));
    }
    if (isOptionChange && _state.hasIn(["items", drawingUid])) {
      _state.setIn(["items", drawingUid, "isRateStale"], true);
    }
  };

  setAdditionalPrices = (_state, payload) => {
    _state.setIn(["orderPrices", "additionalPrices"], fromJS(payload));

    const sumAdditionalPrices = _state
      .getIn(["orderPrices", "additionalPrices"])
      .reduce((sum, price) => {
        if (!price.get("isDeleted")) {
          return Decimal(sum).plus(price.get("value"));
        }

        return Decimal(sum);
      }, Decimal(0));
    _state.setIn(
      ["orderPrices", "sumAdditionalPrices"],
      sumAdditionalPrices.toDecimalPlaces(2).toFixed(2)
    );
  };

  setDiscountPrices = (_state, payload) => {
    _state.setIn(["orderPrices", "discountPrices"], fromJS(payload));

    // Keep sumDiscountPercent for backward compatibility (sum of percent-only discounts)
    // Very very legacy codes in the past
    // sumDiscountPercent is not valid anymore since the discounts are now a combination of fixed dollar and percentage value
    // use the above discountPrices array
    // const sumDiscountPercent = _state
    //   .getIn(["orderPrices", "discountPrices"])
    //   .reduce((sum, price) => {
    //     if (!price.get("isDeleted")) {
    //       const percent = price.get("percent");

    //       if (percent) {
    //         return Decimal(sum).plus(percent);
    //       }
    //     }

    //     return Decimal(sum);
    //   }, Decimal(0));
    // _state.setIn(
    //   ["orderPrices", "sumDiscountPercent"],
    //   sumDiscountPercent.toDecimalPlaces(2).toFixed(2)
    // );
  };

  setMarkupPrices = (_state, payload) => {
    _state.setIn(["orderPrices", "markupPrices"], fromJS(payload));
  };

  calcPricesDrawings = (_state) => {
    const { sumDrawingsPrices, sumDrawingsCustomPrices, gstDrawingsPrices } =
      helpers.calcPricesDrawings(_state.get("items").toJS());
    _state.setIn(["orderPrices", "sumDrawingsPrices"], sumDrawingsPrices);
    _state.setIn(["orderPrices", "sumDrawingsCustomPrices"], sumDrawingsCustomPrices);
    _state.setIn(["orderPrices", "gstDrawingsPrices"], gstDrawingsPrices);
  };

  defineReducers() {
    return {
      [ACTIONS.CREATE_NEW_ORDER]: withMutations(this.createNewOrder),

      [ACTIONS.SET_UIDS]: withMutations(this.setUids),

      [ACTIONS.OPEN_ORDER]: withMutations(this.openOrder),

      [ACTIONS.SET_ORDER_PRICES_FROM_SERVER]: withMutations(this.setOrderPricesFromServer),

      [ACTIONS.RESET_DRAWINGS]: withMutations((_state) => {
        _state.set("items", _state.get("startStateDrawings") || fromJS({}));
        _state.setIn(["dataServer", "items"], []);

        this.calcPricesDrawings(_state);
      }),

      [ACTIONS.SET_DATA_AFTER_SAVING_DRAWINGS]: (state) =>
        state.set("startStateDrawings", state.get("items")),

      [ACTIONS.ADD_NEW_DRAWING]: withMutations(this.addNewDrawing),

      [ACTIONS.DUPLICATE_DRAWING]: withMutations(this.duplicateDrawing),

      [ACTIONS.SET_ACTIVE_DRAWING_TEMP_UID]: withMutations(this.setDrawingTempUid),

      [ACTIONS.DELETE_DRAWING]: (state, { payload: { drawingTempUid, isNewDrawing } }) => {
        if (isNewDrawing) {
          return state.deleteIn(["items", drawingTempUid]);
        }

        return state.setIn(["items", drawingTempUid, "isDeleted"], true);
      },

      [ACTIONS.UNDO_DELETE_DRAWING]: (
        state,
        { payload: { drawingTempUid, items, isNewDrawing } }
      ) => {
        if (isNewDrawing) {
          return state.setIn(["items", drawingTempUid], items);
        }

        return state.setIn(["items", drawingTempUid, "isDeleted"], false);
      },

      [`${ACTIONS.SET_PRICE_PER_METER_DRAWING} fulfilled`]: withMutations(
        this.setPricePerMetreDrawing
      ),

      [`${ACTIONS.SET_TOTAL_PRICE_DRAWING} fulfilled`]: withMutations(this.setTotalPriceDrawing),

      [`${ACTIONS.SWITCH_CUSTOM_PRICE} fulfilled`]: withMutations(this.switchCustomPrice),

      [`${ACTIONS.SWTICH_CUSTOM_PRICE_PER_METER} fulfilled`]: withMutations(
        this.switchCustomPricePerMeter
      ),

      [`${ACTIONS.GET_PRICES_BY_PRODUCT} fulfilled`]: withMutations(this.getPricesProducts),

      [`${ACTIONS.GET_PRICES_BY_PRODUCT} pending`]: withMutations(this.setPricesPending),

      [`${ACTIONS.SET_ADDITIONAL_PRICES} fulfilled`]: withMutations(this.setAdditionalPrices),

      [`${ACTIONS.SET_DISCOUNT_PRICES} fulfilled`]: withMutations(this.setDiscountPrices),

      [`${ACTIONS.SET_MARKUP_PRICES} fulfilled`]: withMutations(this.setMarkupPrices),

      [ACTIONS.CALC_PRICE_DRAWINGS]: withMutations(this.calcPricesDrawings),

      [ACTIONS.CHANGE_DRAWING_PRICE]: (state, { payload }) =>
        state.setIn(["isChangedPrice"], payload),
    };
  }
}

const instance = new AuthModule();
instance.init();

export default instance;
